import { useEffect, useState } from "react";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Pencil, Copy, X } from "lucide-react";

const empty = { name: "", address: "", route_code: "" };

export default function Customers() {
    const [customers, setCustomers] = useState([]);
    const [editing, setEditing] = useState(null);
    const [error, setError] = useState("");
    const [loadError, setLoadError] = useState("");

    const load = async () => {
        setLoadError("");
        try {
            setCustomers(asList((await api.get("/customers")).data, ["customers"]));
        } catch (e) {
            setLoadError(formatApiErrorDetail(e.response?.data?.detail ?? "Klanten konden niet worden geladen"));
        }
    };
    useEffect(() => {
        load();
    }, []);

    const save = async () => {
        setError("");
        try {
            if (!editing.name.trim()) return setError("Klantnaam is verplicht");
            if (editing.id) await api.put(`/customers/${editing.id}`, editing);
            else await api.post("/customers", editing);
            setEditing(null);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail ?? "Opslaan mislukt"));
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Klant ${c.name} verwijderen?`)) return;
        try {
            await api.delete(`/customers/${c.id}`);
            await load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail ?? "Verwijderen mislukt"));
        }
    };

    return (
        <div>
            <PageHeader
                kicker="Master Data"
                title="Klanten"
                subtitle="Beheer van klanten met bezorgadres en route-code voor de werkplaats."
                testid="customers-header"
                actions={
                    <button
                        onClick={() => setEditing({ ...empty })}
                        data-testid="btn-new-customer"
                        className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2 transition-colors"
                    >
                        <Plus className="h-4 w-4" /> Nieuwe klant
                    </button>
                }
            />

            {loadError && (
                <div className="mb-5 flex items-center justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    <span>{loadError}</span>
                    <button type="button" onClick={load} className="font-semibold underline">Opnieuw proberen</button>
                </div>
            )}

            <div className="bg-white border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-sm" data-testid="customers-table">
                    <thead>
                        <tr className="bg-ink-50">
                            <th className="text-left px-4 py-3 data-label">Naam</th>
                            <th className="text-left px-4 py-3 data-label">Adres</th>
                            <th className="text-left px-4 py-3 data-label">Route</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {customers.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-5 py-10 text-center text-ink-500">
                                    Nog geen klanten.
                                </td>
                            </tr>
                        )}
                        {customers.map((c) => (
                            <tr
                                key={c.id}
                                className="border-t border-border/60 hover:bg-white"
                                data-testid={`customer-row-${c.id}`}
                            >
                                <td className="px-4 py-3 font-medium text-ink-900">{c.name}</td>
                                <td className="px-4 py-3 text-ink-500">{c.address || "—"}</td>
                                <td className="px-4 py-3 font-mono">{c.route_code || "—"}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => {
                                            const { id: _id, ...rest } = c;
                                            setEditing({ ...rest, name: `${c.name} (kopie)` });
                                        }}
                                        className="text-ink-700 hover:text-florefy-900 p-1"
                                        data-testid={`btn-duplicate-customer-${c.id}`}
                                        title="Dupliceer klant"
                                    >
                                        <Copy className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => setEditing({ ...c })}
                                        className="text-ink-700 hover:text-florefy-900 p-1 ml-1"
                                        data-testid={`btn-edit-customer-${c.id}`}
                                        title="Bewerken"
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => remove(c)}
                                        className="text-ink-700 hover:text-destructive p-1 ml-1"
                                        data-testid={`btn-delete-customer-${c.id}`}
                                        title="Verwijderen"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 bg-florefy-900/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-sm w-full max-w-md p-6 mx-auto" data-testid="customer-modal">
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
                            <h2 className="text-xl font-semibold text-ink-900">
                                {editing.id ? "Klant bewerken" : "Nieuwe klant"}
                            </h2>
                            <button onClick={() => setEditing(null)} data-testid="btn-close-customer-modal">
                                <X className="h-5 w-5 text-ink-700" />
                            </button>
                        </div>
                        {error && (
                            <div className="bg-destructive/10 border border-destructive text-destructive text-sm p-3 mb-3 rounded-sm">
                                {error}
                            </div>
                        )}
                        <div className="space-y-3">
                            <Field label="Naam">
                                <input
                                    className={inputCls}
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    data-testid="input-customer-name"
                                />
                            </Field>
                            <Field label="Adres">
                                <input
                                    className={inputCls}
                                    value={editing.address}
                                    onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                                    data-testid="input-customer-address"
                                />
                            </Field>
                            <Field label="Route-code">
                                <input
                                    className={inputCls}
                                    value={editing.route_code}
                                    onChange={(e) => setEditing({ ...editing, route_code: e.target.value })}
                                    data-testid="input-customer-route"
                                    placeholder="bv. R-NL-01"
                                />
                            </Field>
                        </div>
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                            <button
                                onClick={() => setEditing(null)}
                                className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-ink-50"
                            >
                                Annuleer
                            </button>
                            <button
                                onClick={save}
                                className="px-4 py-2 rounded-sm bg-florefy-900 hover:bg-florefy-700 text-white text-sm font-medium"
                                data-testid="btn-save-customer"
                            >
                                Opslaan
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls =
    "w-full px-3 py-2 border border-border rounded-sm bg-white font-mono text-sm focus:outline-none focus:ring-1 focus:ring-florefy-900";

function Field({ label, children }) {
    return (
        <div>
            <div className="data-label mb-1">{label}</div>
            {children}
        </div>
    );
}
